import React, { useState, useEffect } from 'react';
import AudioPrompt from './AudioPrompt';

export default function DragDropQuestion({ question, value, onChange, disabled = false }) {
  const { config } = question;
  const audioPrompt = config?.audio_prompt;
  const items = config?.items || [];

  // Current order of items (IDs)
  const [orderedItems, setOrderedItems] = useState([]);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  // Initialize with value or shuffled items
  useEffect(() => {
    if (value && Array.isArray(value)) {
      setOrderedItems(value);
    } else {
      // Shuffle items for initial display
      const shuffled = [...items].sort(() => Math.random() - 0.5);
      setOrderedItems(shuffled.map((item) => item.id));
    }
  }, []);

  const handleDragStart = (e, item, index) => {
    if (disabled) return;
    setDraggedItem({ item, index });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', item.id);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (!draggedItem || disabled) return;

    const newOrder = [...orderedItems];
    const [removed] = newOrder.splice(draggedItem.index, 1);
    newOrder.splice(dropIndex, 0, removed);

    setOrderedItems(newOrder);
    onChange?.(newOrder);
    setDraggedItem(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverIndex(null);
  };

  // Touch support for mobile
  const handleTouchStart = (e, item, index) => {
    if (disabled) return;
    setDraggedItem({ item, index });
  };

  const moveItem = (fromIndex, toIndex) => {
    if (disabled || fromIndex === toIndex) return;

    const newOrder = [...orderedItems];
    const [removed] = newOrder.splice(fromIndex, 1);
    newOrder.splice(toIndex, 0, removed);

    setOrderedItems(newOrder);
    onChange?.(newOrder);
    setDraggedItem(null);
  };

  // Get item data by ID
  const getItemById = (id) => items.find((item) => item.id === id);

  return (
    <div className="space-y-4">
      {/* Audio prompt for young students */}
      {audioPrompt && <AudioPrompt text={audioPrompt} autoPlay />}

      {/* Instructions */}
      <p className="text-sm text-gray-500">
        Drag and drop the items to put them in the correct order, or use the arrows.
      </p>

      {/* Drag and drop area */}
      <div className="space-y-2">
        {orderedItems.map((itemId, index) => {
          const item = getItemById(itemId);
          if (!item) return null;

          const isDragging = draggedItem?.item?.id === item.id;
          const isDragOver = dragOverIndex === index;

          return (
            <div
              key={item.id}
              draggable={!disabled}
              onDragStart={(e) => handleDragStart(e, item, index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
              className={`flex items-center p-3 rounded-lg border-2 transition-all ${
                isDragging
                  ? 'opacity-50 border-indigo-400 bg-indigo-50'
                  : isDragOver
                  ? 'border-indigo-500 bg-indigo-50'
                  : 'border-gray-200 bg-white'
              } ${disabled ? 'cursor-not-allowed' : 'cursor-grab active:cursor-grabbing'}`}
            >
              {/* Position number */}
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center mr-3">
                <span className="text-sm font-medium text-gray-600">{index + 1}</span>
              </div>

              {/* Drag handle */}
              <div className="flex-shrink-0 mr-3 text-gray-400">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M7 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 2zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 14zm6-8a2 2 0 1 0-.001-4.001A2 2 0 0 0 13 6zm0 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 14z" />
                </svg>
              </div>

              {/* Item content */}
              <div className="flex-1 flex items-center">
                {item.image ? (
                  <img
                    src={item.image}
                    alt={item.label || item.id}
                    className="h-12 w-12 object-contain"
                  />
                ) : (
                  <span className="text-lg font-medium text-gray-900">
                    {item.label || item.id}
                  </span>
                )}
              </div>

              {/* Move buttons for accessibility/mobile */}
              {!disabled && (
                <div className="flex-shrink-0 flex flex-col ml-2">
                  <button
                    type="button"
                    onClick={() => moveItem(index, Math.max(0, index - 1))}
                    disabled={index === 0}
                    className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                    aria-label="Move up"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => moveItem(index, Math.min(orderedItems.length - 1, index + 1))}
                    disabled={index === orderedItems.length - 1}
                    className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                    aria-label="Move down"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Accessibility */}
      <div className="sr-only">
        {orderedItems.map((itemId, index) => {
          const item = getItemById(itemId);
          return (
            <span key={itemId}>
              Position {index + 1}: {item?.label || itemId}.{' '}
            </span>
          );
        })}
      </div>
    </div>
  );
}
